import { useState, useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  CheckCheck,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";

import { ClientCatalogTab } from "@/components/features/clients/ClientCatalogTab";
import { ClientSummaryTab } from "@/components/features/clients/ClientSummaryTab";
import { isProOrUltraPlan } from "@/components/shared/PaidPlanRoute";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  invoiceCreationBlockedTooltipKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { useReissueRejectedInvoice } from "@/hooks/useReissueRejectedInvoice";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { htmlToPlainText } from "@/lib/html-text";
import { clientKeys, invoiceKeys, tenantKeys } from "@/lib/queryKeys";
import { formatInvoiceEmailsForDisplay } from "@/utils/invoice-email-addresses";
import { getTenantCurrent } from "@/services/authService";
import { getClient, deleteClient } from "@/services/clientService";
import { listInvoices } from "@/services/invoiceService";
import { isCreditNote, isDebitNote } from "@fiscalo/shared-types";
import {
  statusVariant as sharedStatusVariant,
  statusIcon as sharedStatusIcon,
  formatComprobanteLabel as sharedFormatComprobanteLabel,
  formatTotal as sharedFormatTotal,
  buildInvoiceListActions,
  groupInvoicesWithChildren,
  invoiceHasAdjustments,
  resolveInvoiceDisplayStatus,
  type ReissueRef,
} from "@/pages/invoices/invoice-list-shared";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const formatComprobanteLabel = sharedFormatComprobanteLabel;
const statusVariant = sharedStatusVariant;
const statusIcon = sharedStatusIcon;
const formatTotal = sharedFormatTotal;

const DOCUMENT_TYPE_KEYS: Record<string, string> = {
  RUT: "clients.form.documentTypeRut",
  CI: "clients.form.documentTypeCi",
  EXT: "clients.form.documentTypeExt",
};

const PAYMENT_TERM_KEYS: Record<number, string> = {
  0: "clients.form.paymentTerm0",
  30: "clients.form.paymentTerm30",
  60: "clients.form.paymentTerm60",
  90: "clients.form.paymentTerm90",
};

const CURRENCY_KEYS: Record<string, string> = {
  USD: "clients.form.defaultCurrencyUsd",
  UYU: "clients.form.defaultCurrencyUyu",
};

function invoiceEmailTemplateLabel(
  template: string | undefined,
  t: TFunction
): string {
  switch (template) {
    case "minimal":
      return t("clients.form.invoiceEmailTemplateDefault");
    case "custom":
      return t("clients.form.invoiceEmailTemplateCustom");
    default:
      return t("clients.form.invoiceEmailTemplateDefault");
  }
}

function truncatePreview(text: string | null | undefined, max: number): string {
  const s = (text ?? "").trim();
  if (!s) return "";
  return s.length <= max ? s : `${s.slice(0, max)}…`;
}

function DetailRow({
  label,
  value,
  valueAsLink,
}: {
  label: string;
  value: React.ReactNode;
  valueAsLink?: string;
}): JSX.Element {
  const content =
    valueAsLink != null && typeof value === "string" ? (
      <a
        href={valueAsLink}
        className="text-primary underline underline-offset-2 transition-colors hover:text-primary/90"
      >
        {value}
      </a>
    ) : (
      value
    );
  return (
    <div className="grid grid-cols-[minmax(0,140px)_1fr] gap-3 py-2 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,180px)_1fr]">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{content ?? "—"}</dd>
    </div>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border">{children}</dl>
      </CardContent>
    </Card>
  );
}

const TAB_SUMMARY = "summary";
const TAB_DETAILS = "details";
const TAB_INVOICES = "invoices";
const TAB_PRODUCTS = "products";

function ReissueChipInline({
  reissueRef,
  t,
  navigate,
}: {
  reissueRef: ReissueRef;
  t: TFunction;
  navigate: (path: string) => void;
}): JSX.Element {
  const targetLabel = sharedFormatComprobanteLabel(
    reissueRef.targetCfeType,
    reissueRef.targetInternalNumber,
    t
  );
  const text =
    reissueRef.direction === "successorOf"
      ? t("invoices.list.reissueOf", "Reemite {{label}}", { label: targetLabel })
      : t("invoices.list.reissuedBy", "Reemitida por {{label}}", { label: targetLabel });
  return (
    <button
      type="button"
      data-interactive
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/invoices/${reissueRef.targetId}`);
      }}
      className="inline-flex shrink-0 items-center gap-1 rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 text-xs font-normal text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground"
    >
      <RotateCcw className="h-3 w-3" aria-hidden />
      <span>{text}</span>
    </button>
  );
}

export function ClientDetailsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customMessageDialogOpen, setCustomMessageDialogOpen] = useState(false);
  const [reissueDialogInvoiceId, setReissueDialogInvoiceId] = useState<
    string | null
  >(null);
  const [expandedRejectedAncestors, setExpandedRejectedAncestors] = useState<Set<string>>(
    () => new Set()
  );
  const toggleRejectedAncestor = useCallback((primaryId: string): void => {
    setExpandedRejectedAncestors((prev) => {
      const next = new Set(prev);
      if (next.has(primaryId)) next.delete(primaryId);
      else next.add(primaryId);
      return next;
    });
  }, []);
  const pagination = usePaginationFromUrl();

  const tabParam = searchParams.get("tab");
  const activeTab =
    tabParam === TAB_INVOICES
      ? TAB_INVOICES
      : tabParam === TAB_PRODUCTS
        ? TAB_PRODUCTS
        : tabParam === TAB_DETAILS
          ? TAB_DETAILS
          : TAB_SUMMARY;

  const setTab = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === TAB_SUMMARY) next.delete("tab");
          else next.set("tab", value);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { user } = useAuth();
  const { data: tenant, isFetched: tenantPlanFetched } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const canUseInvoiceEmailTemplates =
    tenantPlanFetched && isProOrUltraPlan(tenant?.planId);

  const { reissue: reissueRejected } = useReissueRejectedInvoice();

  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();
  const disabledInvoiceTooltipKey =
    invoiceCreationBlockedTooltipKey(invoicingBlockedReason);

  const { data: client, isLoading, isError } = useQuery({
    queryKey: clientKeys.detail(id ?? ""),
    queryFn: () => getClient(id!),
    enabled: Boolean(id),
  });

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    isFetching: invoicesFetching,
    isError: invoicesError,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: invoiceKeys.list({
      page: pagination.page,
      limit: pagination.limit,
      customerId: id ?? undefined,
    }),
    queryFn: () =>
      listInvoices({
        page: pagination.page,
        limit: pagination.limit,
        customerId: id ?? undefined,
      }),
    enabled: Boolean(id) && activeTab === TAB_INVOICES,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (invoicesData != null) {
      pagination.setTotal(invoicesData.total);
    } else if (invoicesError) {
      pagination.setTotal(0);
    }
  }, [invoicesData, invoicesError, pagination]);

  useEffect(() => {
    if (invoicesError) {
      toast.error(
        t("invoices.list.error", "Unable to load invoices. The API may not be available yet.")
      );
    }
  }, [invoicesError, t]);

  const invoices = useMemo(() => {
    const list = invoicesData?.data ?? [];
    return [...list].sort((a, b) => {
      const aDate = a.issueDate ?? "";
      const bDate = b.issueDate ?? "";
      return bDate.localeCompare(aDate);
    });
  }, [invoicesData?.data]);

  const deleteMutation = useMutation({
    mutationFn: () => deleteClient(id!),
    onSuccess: () => {
      toast.success(t("clients.detail.deleted", "Client removed."));
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
      navigate("/clients");
    },
    onError: () => {
      toast.error(
        t("clients.detail.deleteError", "Could not remove client. Please try again.")
      );
    },
  });

  const handleDelete = (): void => {
    if (id) deleteMutation.mutate();
  };

  if (!id) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("clients.detail.invalidId", "Invalid client")}
          onBack={() => navigate("/clients")}
        />
      </div>
    );
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError || !client) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("clients.detail.title", "Client")}
          onBack={() => navigate("/clients")}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          {t("clients.detail.notFound", "Client not found.")}
        </p>
      </div>
    );
  }

  const documentTypeLabel =
    DOCUMENT_TYPE_KEYS[client.documentType] != null
      ? t(DOCUMENT_TYPE_KEYS[client.documentType])
      : client.documentType;
  const paymentTermLabel =
    client.paymentTermDays != null && PAYMENT_TERM_KEYS[client.paymentTermDays] != null
      ? t(PAYMENT_TERM_KEYS[client.paymentTermDays])
      : client.paymentTermDays != null
        ? String(client.paymentTermDays)
        : null;
  const currencyLabel =
    client.defaultCurrency != null && CURRENCY_KEYS[client.defaultCurrency] != null
      ? t(CURRENCY_KEYS[client.defaultCurrency])
      : client.defaultCurrency ?? null;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={client.displayName ?? client.legalName}
        onBack={() => navigate("/clients")}
        actions={
          <div className="flex gap-2">
            {invoicingBlocked && disabledInvoiceTooltipKey ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button type="button" disabled aria-disabled="true">
                      <FileText className="mr-2 h-4 w-4" />
                      {t("clients.detail.invoice", "Create invoice")}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t(disabledInvoiceTooltipKey)}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                onClick={() =>
                  navigate("/invoices/new", { state: { client } })
                }
              >
                <FileText className="mr-2 h-4 w-4" />
                {t("clients.detail.invoice", "Create invoice")}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(`/clients/${id}/edit`)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t("clients.detail.edit", "Edit")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("clients.detail.delete", "Delete")}
            </Button>
          </div>
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={setTab}
        className="w-full"
      >
        <TabsList className="mb-4">
          <TabsTrigger value={TAB_SUMMARY}>
            {t("clients.detail.tabs.summary", "Summary")}
          </TabsTrigger>
          <TabsTrigger value={TAB_DETAILS}>
            {t("clients.detail.tabs.details", "Details")}
          </TabsTrigger>
          <TabsTrigger value={TAB_PRODUCTS}>
            {t("clients.detail.tabs.products", "Products / services")}
          </TabsTrigger>
          <TabsTrigger value={TAB_INVOICES}>
            {t("clients.detail.tabs.invoices", "Invoices")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={TAB_SUMMARY} className="mt-0">
          {id ? <ClientSummaryTab customerId={id} /> : null}
        </TabsContent>

        <TabsContent value={TAB_DETAILS} className="mt-0 space-y-4">
        <DetailSection title={t("clients.form.legalInfo", "Legal information")}>
          <DetailRow
            label={t("clients.form.legalName", "Razón social")}
            value={client.legalName}
          />
          {client.displayName != null && client.displayName.trim() !== "" && (
            <DetailRow
              label={t("clients.form.displayName", "Display / fantasy name")}
              value={client.displayName}
            />
          )}
          <DetailRow
            label={t("clients.form.documentType", "Document type")}
            value={documentTypeLabel}
          />
          {client.documentType === "EXT" &&
            client.countryCode != null &&
            client.countryCode.trim() !== "" && (
              <DetailRow
                label={t("clients.form.country", "Country")}
                value={client.countryCode}
              />
            )}
          <DetailRow
            label={t("clients.form.taxIdOrDocument", "RUT / EIN")}
            value={
              client.documentType === "EXT"
                ? (client.docRecepExt?.trim() ?? "—")
                : (client.rut ?? "—")
            }
          />
          <DetailRow
            label={t("clients.form.address", "Address")}
            value={(client.address ?? "").trim() || "—"}
          />
        </DetailSection>

        <DetailSection title={t("clients.form.contactInfo", "Contact information")}>
          <DetailRow
            label={t("clients.form.email", "Email")}
            value={client.email?.trim() ?? "—"}
            valueAsLink={
              client.email?.trim() ? `mailto:${client.email.trim()}` : undefined
            }
          />
          <DetailRow
            label={t("clients.form.phone", "Phone")}
            value={(client.phone ?? "").trim() || "—"}
          />
        </DetailSection>

        <DetailSection
          title={t("clients.form.invoicingDetails", "Data for invoicing")}
        >
          <DetailRow
            label={t("clients.form.defaultCurrency", "Default currency")}
            value={currencyLabel ?? "—"}
          />
          <DetailRow
            label={t("clients.form.paymentType", "Forma de pago (plazos)")}
            value={paymentTermLabel ?? "—"}
          />
          <DetailRow
            label={t("clients.form.discountPercent", "Discount (%)")}
            value={
              client.discountPercent != null ? `${client.discountPercent}%` : "—"
            }
          />
          <DetailRow
            label={t("clients.form.languageLabel", "Idioma del cliente")}
            value={
              client.language === "en"
                ? t("clients.form.languageEn", "Inglés")
                : t("clients.form.languageEs", "Español")
            }
          />
          <DetailRow
            label={t("clients.form.pdfFilenameLabel", "Formato del nombre")}
            value={
              client.pdfFilenameTemplate?.trim()
                ? client.pdfFilenameTemplate
                : t("clients.detail.pdfFilenameDefault", "Predeterminado")
            }
          />
          <DetailRow
            label={t("clients.form.sendInvoiceByEmail", "Send invoice by email")}
            value={
              client.sendInvoiceByEmail === true
                ? t("clients.detail.yes", "Yes")
                : client.sendInvoiceByEmail === false
                  ? t("clients.detail.no", "No")
                  : "—"
            }
          />
          {client.sendInvoiceByEmail === true && (
            <>
              <DetailRow
                label={t("clients.form.autoSendInvoiceEmail", "Send automatically")}
                value={
                  client.autoSendInvoiceEmail === false
                    ? t("clients.detail.no", "No")
                    : t("clients.detail.yes", "Yes")
                }
              />
              <DetailRow
                label={t("clients.form.invoiceEmailLabel", "Email for invoice")}
                value={
                  client.invoiceEmail?.trim()
                    ? formatInvoiceEmailsForDisplay(client.invoiceEmail)
                    : client.email?.trim() || "—"
                }
              />
              {client.invoiceEmailCc?.trim() ? (
                <DetailRow
                  label={t("clients.form.invoiceEmailCcLabel")}
                  value={formatInvoiceEmailsForDisplay(client.invoiceEmailCc)}
                />
              ) : null}
              {!tenantPlanFetched ? null : canUseInvoiceEmailTemplates ? (
                <>
                  <DetailRow
                    label={t("clients.form.invoiceEmailTemplateLabel", "Email template")}
                    value={invoiceEmailTemplateLabel(client.invoiceEmailTemplate, t)}
                  />
                  {client.invoiceEmailTemplate === "custom" && (
                    <DetailRow
                      label={t("clients.form.invoiceEmailCustomSubjectLabel", "Email subject")}
                      value={
                        client.invoiceEmailCustomSubject?.trim()
                          ? client.invoiceEmailCustomSubject
                          : t("clients.detail.invoiceEmailSubjectUsesDefault")
                      }
                    />
                  )}
                  {client.invoiceEmailTemplate === "custom" && (
                    <DetailRow
                      label={t("clients.form.invoiceEmailIncludeCompanyLogo")}
                      value={
                        client.invoiceEmailIncludeCompanyLogo === false
                          ? t("clients.detail.no", "No")
                          : t("clients.detail.yes", "Yes")
                      }
                    />
                  )}
                  {client.invoiceEmailTemplate === "custom" && (
                    <DetailRow
                      label={t("clients.form.invoiceEmailIncludeInvoiceDetails")}
                      value={
                        client.invoiceEmailIncludeInvoiceDetails === false
                          ? t("clients.detail.no", "No")
                          : t("clients.detail.yes", "Yes")
                      }
                    />
                  )}
                  {client.invoiceEmailTemplate === "custom" && (
                    <DetailRow
                      label={t("clients.detail.customInvoiceMessage", "Custom message")}
                      value={
                        <div className="space-y-2">
                          <p className="whitespace-pre-wrap break-words text-sm text-foreground">
                            {htmlToPlainText(client.invoiceEmailCustomBody) === ""
                              ? "—"
                              : truncatePreview(
                                  htmlToPlainText(client.invoiceEmailCustomBody),
                                  160
                                )}
                          </p>
                          {htmlToPlainText(client.invoiceEmailCustomBody).length > 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="transition-colors duration-150"
                              onClick={() => setCustomMessageDialogOpen(true)}
                            >
                              {t("clients.detail.viewCustomMessage", "View full message")}
                            </Button>
                          )}
                        </div>
                      }
                    />
                  )}
                </>
              ) : (
                <DetailRow
                  label={t("clients.form.invoiceEmailTemplateLabel", "Email template")}
                  value={
                    <span className="text-muted-foreground">
                      {t("clients.detail.invoiceEmailTemplatesPlanHint")}{" "}
                      <Link
                        to="/settings?tab=billing"
                        className="text-primary underline underline-offset-2 transition-colors hover:text-primary/90"
                      >
                        {t("clients.form.invoiceEmailTemplatesUpgradeCta")}
                      </Link>
                    </span>
                  }
                />
              )}
            </>
          )}
        </DetailSection>

        <Dialog open={customMessageDialogOpen} onOpenChange={setCustomMessageDialogOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {t("clients.detail.customInvoiceMessage", "Custom message")}
              </DialogTitle>
            </DialogHeader>
            <pre className="whitespace-pre-wrap break-words text-sm text-foreground">
              {htmlToPlainText(client.invoiceEmailCustomBody)}
            </pre>
          </DialogContent>
        </Dialog>

        {(client.createdAt != null || client.updatedAt != null) && (
          <DetailSection title={t("clients.detail.record", "Record")}>
            {client.createdAt != null && (
              <DetailRow
                label={t("clients.detail.addedOn", "Added on")}
                value={formatDisplayDate(client.createdAt, {
                  locale: i18n.language,
                })}
              />
            )}
            {client.updatedAt != null && (
              <DetailRow
                label={t("clients.detail.updatedOn", "Updated on")}
                value={formatDisplayDate(client.updatedAt, {
                  locale: i18n.language,
                })}
              />
            )}
          </DetailSection>
        )}
        </TabsContent>

        <TabsContent value={TAB_PRODUCTS} className="mt-0">
          {id ? <ClientCatalogTab clientId={id} /> : null}
        </TabsContent>

        <TabsContent value={TAB_INVOICES} className="mt-0">
          <TableCard>
            {invoicesLoading ? (
              <div className="flex min-h-[40vh] items-center justify-center py-12">
                <LoadingLogo size="lg" />
              </div>
            ) : invoices.length === 0 ? (
              <EmptyState
                title={t("clients.detail.invoicesEmpty", "No invoices for this client.")}
                action={
                  invoicingBlocked && disabledInvoiceTooltipKey ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-block">
                          <Button
                            type="button"
                            disabled
                            aria-disabled="true"
                            className="inline-flex"
                          >
                            <Plus className="mr-2 h-4 w-4" aria-hidden />
                            {t("clients.detail.invoicesEmptyAction", "Create invoice")}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t(disabledInvoiceTooltipKey)}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Button
                      onClick={() => navigate("/invoices/new", { state: { client } })}
                      className="inline-flex"
                    >
                      <Plus className="mr-2 h-4 w-4" aria-hidden />
                      {t("clients.detail.invoicesEmptyAction", "Create invoice")}
                    </Button>
                  )
                }
              />
            ) : (
              <div className="relative">
                {invoicesFetching && (
                  <div
                    className="absolute inset-0 z-10 flex min-h-[200px] items-center justify-center rounded-lg bg-background/80 backdrop-blur-[1px]"
                    aria-busy="true"
                    aria-label={t("invoices.list.loading", "Loading…")}
                  >
                    <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("invoices.list.date", "Date")}</TableHead>
                      <TableHead>{t("invoices.list.comprobante", "Document")}</TableHead>
                      <TableHead>{t("invoices.list.status", "Status")}</TableHead>
                      <TableHead className="text-right">{t("invoices.list.total", "Total")}</TableHead>
                      <TableHead className="w-[52px] text-right" aria-hidden />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const groupedRows = groupInvoicesWithChildren(invoices);
                      let currentPrimaryId: string | null = null;
                      return groupedRows.filter((row) => {
                        if (!row.isChild) {
                          currentPrimaryId = row.inv.id;
                          return true;
                        }
                        if (row.childKind === "rejectedAncestor") {
                          return currentPrimaryId
                            ? expandedRejectedAncestors.has(currentPrimaryId)
                            : false;
                        }
                        return true;
                      });
                    })().map((row) => {
                      const { inv, isChild, childKind, reissueRef, hasRejectedAncestor, rejectedAncestorCount } = row;
                      const isRejectedAncestor = childKind === "rejectedAncestor";
                      const isAncestorExpanded = expandedRejectedAncestors.has(inv.id);
                      const ancestorCount = rejectedAncestorCount ?? 0;
                      const displayStatus = resolveInvoiceDisplayStatus(inv);
                      const StatusIcon = statusIcon(displayStatus);
                      const listLabel =
                        t(`invoices.list.statusLabel.${displayStatus}`, displayStatus) ||
                        t(`invoices.status.${displayStatus}`, displayStatus);
                      const isRejected = displayStatus === "rejected";
                      const hasAdj = invoiceHasAdjustments(inv);

                      const rowActions = buildInvoiceListActions(inv, {
                        t,
                        navigate,
                        invoicingBlocked,
                        onReissueRejected: (invoiceId) => {
                          setReissueDialogInvoiceId(invoiceId);
                        },
                        reissueLoadingId: null,
                      });

                      return (
                        <TableRow
                          key={inv.id}
                          onRowClick={() => navigate(`/invoices/${inv.id}`)}
                          className={cn(
                            isChild && "bg-muted/30",
                            isRejectedAncestor && "opacity-60"
                          )}
                        >
                          <TableCell className="text-muted-foreground">
                            {inv.issueDate
                              ? formatDisplayDate(inv.issueDate, {
                                  locale: i18n.language,
                                  useCalendarDate: true,
                                })
                              : "—"}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className={cn("flex flex-wrap items-center gap-1.5", isChild && "pl-4")}>
                              {isChild && (
                                <span className="text-muted-foreground" aria-hidden>{"↳"}</span>
                              )}
                              {isCreditNote(inv.cfeType) && (
                                <Badge variant="secondary" className="text-xs font-medium shrink-0">
                                  {t("invoices.list.creditNoteShort", "NC")}
                                </Badge>
                              )}
                              {isDebitNote(inv.cfeType) && (
                                <Badge variant="secondary" className="text-xs font-medium shrink-0">
                                  {t("invoices.list.debitNoteShort", "ND")}
                                </Badge>
                              )}
                              <span>{formatComprobanteLabel(inv.cfeType, inv.internalNumber, t)}</span>
                              {hasAdj && (
                                <span className="text-xs text-muted-foreground shrink-0">
                                  {t("invoices.list.adjusted", "Adjusted")}
                                </span>
                              )}
                              {/* Mirror of invoices-page: hide successor chip once the retry is "Enviada". */}
                              {reissueRef &&
                                !(
                                  reissueRef.direction === "successorOf" &&
                                  displayStatus === "sent"
                                ) && (
                                <ReissueChipInline
                                  reissueRef={reissueRef}
                                  t={t}
                                  navigate={navigate}
                                />
                              )}
                              {hasRejectedAncestor && (
                                <button
                                  type="button"
                                  data-interactive
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRejectedAncestor(inv.id);
                                  }}
                                  className="ml-1 inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                                >
                                  {isAncestorExpanded ? (
                                    <ChevronDown className="h-3 w-3" aria-hidden />
                                  ) : (
                                    <ChevronRight className="h-3 w-3" aria-hidden />
                                  )}
                                  {isAncestorExpanded
                                    ? ancestorCount === 1
                                      ? t("invoices.list.hideRejectedAttempt", "Ocultar intento")
                                      : t("invoices.list.hideRejectedAttempts", "Ocultar intentos")
                                    : ancestorCount === 1
                                      ? t("invoices.list.viewRejectedAttempt", "Ver intento rechazado")
                                      : t("invoices.list.viewRejectedAttemptsCount", "Ver {{count}} intentos rechazados", { count: ancestorCount })}
                                </button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="w-[200px] min-w-[200px] max-w-[200px]">
                            <div className="flex min-w-0 max-w-full">
                              {(() => {
                                const raw = inv as unknown as Record<string, unknown>;
                                const receptorAck = raw.receptorAckCfeEstado as string | undefined;
                                const acuseRecibido = !!raw.acuseReciboReceivedAt;
                                if (isRejected) {
                                  return (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex min-w-0 max-w-full">
                                          <StatusBadge variant={statusVariant(displayStatus)} className="inline-flex max-w-full">
                                            <StatusIcon className="h-3 w-3 shrink-0" aria-hidden />
                                            <span className="min-w-0 truncate">{listLabel}</span>
                                          </StatusBadge>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-[280px]">
                                        {t("invoices.list.rejectedBadgeTooltip", "This invoice was rejected by DGI and has no fiscal effect. A new invoice must be issued.")}
                                      </TooltipContent>
                                    </Tooltip>
                                  );
                                }
                                if (receptorAck === "AE") {
                                  return (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex min-w-0 max-w-full">
                                          <StatusBadge variant="success" className="inline-flex max-w-full">
                                            <CheckCheck className="h-3 w-3 shrink-0" aria-hidden />
                                            <span className="min-w-0 truncate">{t("invoices.list.sentReceivedAndAccepted", "Enviada y aceptada")}</span>
                                          </StatusBadge>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-[280px]">
                                        {t("invoices.list.sentReceivedAndAcceptedTooltip", "El receptor confirmó la recepción y aceptó la factura con un ACKCFE firmado (DGI §XVIII).")}
                                      </TooltipContent>
                                    </Tooltip>
                                  );
                                }
                                if (receptorAck === "RE") {
                                  return (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex min-w-0 max-w-full">
                                          <StatusBadge variant="warning" className="inline-flex max-w-full">
                                            <StatusIcon className="h-3 w-3 shrink-0" aria-hidden />
                                            <span className="min-w-0 truncate">{t("invoices.list.rejectedByReceptor", "Rechazada por receptor")}</span>
                                          </StatusBadge>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-[280px]">
                                        {t("invoices.list.rejectedByReceptorTooltip", "El receptor rechazó la factura con un ACKCFE firmado. La factura mantiene efecto fiscal frente a DGI pero el cliente no la reconoce.")}
                                      </TooltipContent>
                                    </Tooltip>
                                  );
                                }
                                if (acuseRecibido) {
                                  return (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex min-w-0 max-w-full">
                                          <StatusBadge variant="success" className="inline-flex max-w-full">
                                            <CheckCheck className="h-3 w-3 shrink-0" aria-hidden />
                                            <span className="min-w-0 truncate">{t("invoices.list.sentAndReceived", "Enviada y recibida")}</span>
                                          </StatusBadge>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-[280px]">
                                        {t("invoices.list.sentAndReceivedTooltip", "La factura fue enviada al receptor y se recibió el acuse de recibo confirmando la recepción. Aplica solo para empresas uruguayas con RUT registrado en DGI.")}
                                      </TooltipContent>
                                    </Tooltip>
                                  );
                                }
                                return (
                                  <StatusBadge variant={statusVariant(displayStatus)} className="inline-flex max-w-full">
                                    <StatusIcon className="h-3 w-3 shrink-0" aria-hidden />
                                    <span className="min-w-0 truncate">{listLabel}</span>
                                  </StatusBadge>
                                );
                              })()}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {formatTotal(
                              inv.totals?.total?.currency,
                              inv.totals?.total?.amount
                            )}
                          </TableCell>
                          <TableCell
                            className="w-[52px] text-right"
                            onClick={(e) => e.stopPropagation()}
                            data-interactive
                          >
                            <div className="flex justify-end">
                              <TableActionsMenu actions={rowActions} />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  pagination={pagination}
                  rowsPerPageLabel={t("invoices.list.rowsPerPage", "Rows per page")}
                  pageOfLabel={t("invoices.list.pageOf", "{{from}}-{{to}} of {{total}}", {
                    from: (pagination.page - 1) * pagination.limit + 1,
                    to: Math.min(pagination.page * pagination.limit, pagination.total),
                    total: pagination.total,
                  })}
                  onRefresh={() => void refetchInvoices()}
                  isRefreshing={invoicesFetching}
                />
              </div>
            )}
          </TableCard>
        </TabsContent>
      </Tabs>

      <ConfirmationDialog
        open={reissueDialogInvoiceId != null}
        onOpenChange={(open) => {
          if (!open) setReissueDialogInvoiceId(null);
        }}
        title={t("invoices.reissue.confirmTitle")}
        description={t("invoices.reissue.confirmDescription")}
        confirmText={t("invoices.reissue.confirmButton")}
        cancelText={t("pages.common.cancel")}
        onConfirm={() => {
          if (!reissueDialogInvoiceId) return;
          setReissueDialogInvoiceId(null);
          reissueRejected({ invoiceId: reissueDialogInvoiceId });
        }}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t("clients.detail.deleteConfirmTitle", "Delete client?")}
        description={t(
          "clients.detail.deleteConfirmDescription",
          "This will permanently remove this client. You can add them again later if needed."
        )}
        confirmText={t("clients.detail.delete", "Delete")}
        cancelText={t("clients.form.cancel", "Cancel")}
        loadingText={t("clients.detail.deleting", "Deleting…")}
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
